// @flow

import type { UUID } from 'esm-web-pro/types/common';
import { handleActions } from 'redux-actions';
import {
    INITIATE_CALL,
    SET_RINGING,
    STOP_CALL,
    START_CALL,
    SET_MUTED,
    SET_HELD,
    SET_OPERATORS,
    UPDATE_OPERATOR_PRESENCE,
    SET_REGISTERED, SET_CALLEE,
    SET_CALLINGS_STATE, SET_CALLING_ENTITY, SEND_DTMF_REQUEST, SEND_DTMF_SUCCESS,
    TRANSFER, SET_SESSION_ESTABLISHED, SET_START_TIME, CLEAR_COOLDOWN_STATE,
} from 'esm-web-pro/redux/callings/constants';
import PRESENCES from 'esm-web-pro/containers/panels/calling/presence/presences';

export const INITIATED = 'INITIATED';
export const RINGING = 'RINGING';
export const IN_PROCESS = 'IN_PROCESS';

// Call directions
export const INBOUND = 'inbound';
export const OUTBOUND = 'outbound';
export const INTERNAL = 'internal';

export type OperatorsState = {
    [id: string]: {
        avatar: string,
        pbx_username: string,
        name: string,
        status: $Keys<typeof PRESENCES>,
        uuid: string,
    }
}

export type CallState = {
    +entityUuid: UUID,
    +taskUuid: UUID,
    +contactDetailUuid: UUID,
    +entityCandidates: UUID[],
    +status: string,
    +type: string,
    +muted: boolean,
    +held: boolean,
    +transfer?: string,
    +transferred?: boolean,
    +sessionEstablished?: boolean, // If SIP SessionState is established
    +holdPending: boolean,
    +startTime?: Date,

    // Keeps all DTMF requests made during a call
    +dtmfRequests: string[],

    // Indices of the DTMF requests which are already sent
    +dtmfRequestsSent: number[],
} | null;

export type CooldownState = {
    +taskUuid: UUID,
} | null;

export type State = ?{
    +registered: boolean,
    +status: $Keys<typeof PRESENCES>,
    +call: CallState,
    +operators: OperatorsState,
    +cooldownState: CooldownState,
}


const initialState: State = { call: null, operators: {}, cooldownState: null };

const reducer = handleActions({
    [INITIATE_CALL]: (state: State, {
        payload: {
            entityUuid, type, contactDetailUuid, taskUuid, phoneNumber,
        },
    }) => ({
        ...state,
        call: {
            entityUuid, status: INITIATED, type, contactDetailUuid, taskUuid, phoneNumber,
        },
    }),
    [SET_RINGING]: (state: State, {
        payload: {
            entityUuid, type, entityCandidates, callId, transferred,
        },
    }) => {
        const newState = { ...state, call: state.call ? { ...state.call } : {} };
        newState.call.callId = callId;
        newState.call.transferred = transferred;
        if (entityUuid) {
            newState.call.entityUuid = entityUuid;
        }

        if (type) {
            newState.call.type = type;
        }

        if (entityCandidates) {
            newState.call.entityCandidates = entityCandidates;
        }

        newState.call.status = RINGING;
        return newState;
    },
    [START_CALL]: (state: State, { payload }: {
        payload: {}
    }) => {
        const cooldownState = state.call.taskUuid ? { taskUuid: state.call.taskUuid } : {};
        return {
            ...state,
            call: {
                ...state.call,
                startTime: (state.call.type === OUTBOUND && !state.call.transferred) ? undefined : new Date().getTime(),
                status: IN_PROCESS,
                muted: undefined,
                held: undefined,
                holdPending: undefined,
                dtmfRequests: [],
                dtmfRequestsSent: [],
            },
            cooldownState,
        };
    },
    [SET_CALLEE]: (state: State, { payload: { callee } }) => ({
        ...state, call: { ...state.call, callee },
    }),
    [SET_START_TIME]: (state: State, { payload }) => ({
        ...state, call: { ...state.call, startTime: new Date().getTime() },
    }),
    [SET_MUTED]: (state: State, { payload }) => ({
        ...state, call: { ...state.call, muted: payload.muted, holdPending: payload.pending },
    }),
    [SET_HELD]: (state: State, { payload }) => ({
        ...state, call: { ...state.call, held: payload.held, holdPending: payload.pending },
    }),
    [SEND_DTMF_REQUEST]: (state: State, { payload: { value } }) => ({
        ...state, call: { ...state.call, dtmfRequests: [...state.call.dtmfRequests, value] },
    }),
    [SEND_DTMF_REQUEST]: (state: State, { payload: { value } }) => ({
        ...state, call: { ...state.call, dtmfRequests: [...state.call.dtmfRequests, value] },
    }),
    [SEND_DTMF_SUCCESS]: (state: State, { payload: { index } }) => ({
        ...state, call: { ...state.call, dtmfRequestsSent: [...state.call.dtmfRequestsSent, index] },
    }),
    [STOP_CALL]: (state: State, { payload }: {
        payload: {}
    }) => ({ ...state, call: null }),
    [CLEAR_COOLDOWN_STATE]: (state: State, { payload }: {
        payload: {}
    }) => ({ ...state, cooldownState: null }),
    [SET_OPERATORS]: (state: State, { payload: { operators } }) => ({
        ...state, operators,
    }),
    [UPDATE_OPERATOR_PRESENCE]: (state: State, { payload: { id, status } }) => ({
        ...state, operators: { ...state.operators, [id]: { ...state.operators[id], status } },
    }),
    [SET_CALLINGS_STATE]: (state: State, { payload: { state: payloadState } }) => ({ ...payloadState }),
    [SET_REGISTERED]: (state: State, { payload: { registered } }) => ({
        ...state, registered,
    }),
    [SET_CALLING_ENTITY]: (state: State, { payload: { entityUuid } }) => {
        if (!state.call) {
            return state;
        }

        return { ...state, call: { ...state.call, entityUuid } };
    },
    [TRANSFER]: (state: State, { payload: { number } }) => {
        if (!state.call) {
            return state;
        }
        return { ...state, call: { ...state.call, transfer: number } };
    },
    [SET_SESSION_ESTABLISHED]: (state: State, { payload: { sessionEstablished } }) => {
        if (!state.call) {
            return state;
        }
        return { ...state, call: { ...state.call, sessionEstablished } };
    },
}, initialState);

export default reducer;
